/** Bausteine des Bereichs "Schule" (BE-04): "Heute in der Schule" und die Fächer als Karten. */
import { Link } from 'react-router-dom';

import { AreaDot } from '@/modules/areas/AreaDot';
import { DeadlineBadge } from '@/modules/deadlines/DeadlineBadge';
import { NotenWert } from '@/modules/school/components/Noten';
import type { SchuleDaten } from '@/modules/school/useSchuleDaten';
import { countdown, klausurDatum } from '@/domain/school/exams';
import { pfad } from '@/app/fokus';
import { formatDe, today } from '@/lib/datetime';

export function HeuteSchule({ d }: { d: SchuleDaten }) {
  const { woche, ferienHeute, heuteStunden, kommendeFerien, naechsteKlausur, faecher } = d;
  const fachName = new Map(faecher.map((f) => [f.id, f]));
  return (
    <section aria-label="Heute in der Schule" className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="text-sm font-semibold">Heute{woche ? ` (${woche}-Woche)` : ''}</h2>
      {ferienHeute ? (
        <p className="text-sm text-ink-muted">{ferienHeute.name} – keine Schule bis {formatDe(ferienHeute.bis, 'EEEEEE dd.MM.')}.</p>
      ) : heuteStunden.length === 0 ? (
        <p className="text-sm text-ink-faint">Heute keine Schulstunden.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {heuteStunden.map((s) => (
            <li key={s.eventId} className="flex items-center gap-2 text-sm">
              <span className="w-24 shrink-0 tabular-nums text-ink-faint">{s.von}–{s.bis}</span>
              <AreaDot color={fachName.get(s.subjectId)?.color ?? 'var(--color-line-strong)'} size="sm" />
              <span className="min-w-0 flex-1 truncate">{fachName.get(s.subjectId)?.name ?? 'Fach'}</span>
              {s.raum && <span className="shrink-0 text-[12px] text-ink-faint">{s.raum}</span>}
            </li>
          ))}
        </ul>
      )}
      {!ferienHeute && kommendeFerien && (
        <p className="text-[12px] text-ink-faint">Nächste Ferien: {kommendeFerien.name} ab {formatDe(kommendeFerien.von, 'EEEEEE dd.MM.')}</p>
      )}
      {/* Countdown bis zur naechsten Klausur - direkt sichtbar. */}
      <p className="border-t border-line pt-2 text-sm text-ink-muted">
        {naechsteKlausur
          ? <>Nächste Klausur: <span className="text-ink">{naechsteKlausur.title}</span> · <strong className="font-semibold text-accent-text">{countdown(today(), klausurDatum(naechsteKlausur))}</strong></>
          : 'Keine Klausur angesetzt.'}
      </p>
    </section>
  );
}

export function FaecherKarten({ d }: { d: SchuleDaten }) {
  const { faecher, offen, stunden, schnittVon, system } = d;
  return (
    <section aria-label="Fächer" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">Fächer</h2>
      {faecher.length === 0 && <p className="text-sm text-ink-faint">Noch keine Fächer – leg sie in den Einstellungen an.</p>}
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        {faecher.map((f) => {
          const eigene = offen.filter((t) => t.subjectId === f.id);
          const naechste = eigene.find((t) => t.dueDate);
          const schnitt = schnittVon(f.id);
          return (
            <li key={f.id} className="flex flex-col gap-1 rounded-lg border border-line bg-surface px-3 py-2.5">
              <div className="flex items-center gap-2">
                <AreaDot color={f.color} size="sm" />
                {/* Zur Fachseite (Stufe 8) - dort steht alles zum Fach. */}
                <Link to={pfad.fach(f.id)} className="min-w-0 flex-1 truncate text-sm font-medium hover:underline hover:decoration-line-strong">
                  {f.name}
                </Link>
                {schnitt !== null && <NotenWert punkte={schnitt} system={system} titel="Gewichteter Durchschnitt" />}
              </div>
              <p className="text-[12px] text-ink-muted">
                {eigene.length === 0 ? 'Keine offenen Hausaufgaben' : `${eigene.length} offen`}
                {' · '}{stundenText(stunden.filter((s) => s.subjectId === f.id).length)}
              </p>
              {naechste?.dueDate && (
                <p className="flex items-center gap-1.5 text-[12px] text-ink-faint">
                  <span className="min-w-0 flex-1 truncate">Nächste: {naechste.title}</span>
                  <DeadlineBadge date={naechste.dueDate} />
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function stundenText(n: number): string {
  return n === 0 ? 'keine Stunden' : n === 1 ? '1 Stunde im Plan' : `${n} Stunden im Plan`;
}
